import { useMemo, useState } from "react";
import { useSession } from "@core/session/SessionProvider";
import { ApiError } from "@core/http";
import { markErrorHandled, reportError } from "@portal/common/errorMessage";
import type { BilingualText, CatalogEntry, Reward } from "@core/types";
import { tokens } from "@core/theme/tokens";
import { ImageWithPlaceholder } from "@core/media";
import {
  CategoryFilterSelect,
  EditorFooter,
  EditorHeader,
  EditorModal,
  type EditorModalTarget,
  EmptyResultsActions,
  EmptyResultsPanel,
  FieldError,
  FileUploadChip,
  ImagePreviewBox,
  ImageUploadBox,
  InlineNote,
  inputStyle,
  LabeledField as RField,
  placesAndValidityInput,
  PlacesFieldGroup,
  PrizesScreenToolbar,
  resolveBilingualText as text,
  RFieldGroup,
  submitCreateOrEdit,
  RowIconAction,
  useApiFieldErrors,
  useEditorForm,
  usePlacesToggle,
  useToast,
  ValidityRangeFields,
  type ContentLanguage,
} from "@portal/common";
import { useEntries } from "../catalogs/catalogQueries";
import type { RewardInput } from "./api";
import { useRewardMutations, useRewards } from "./rewardQueries";
import {
  REWARD_FORM_FIELDS,
  rewardFormSchema,
  type RewardFormValues,
} from "./rewardSchemas";

function categoryLabel(categories: CatalogEntry[] | undefined, id: string) {
  const found = categories?.find((entry) => entry.id === id);
  return found ? text(found.name, "es") : "—";
}

function formatPoints(value: number) {
  return value.toLocaleString("es-EC");
}

interface RewardsFilterState {
  search: string;
  categoryId: string;
  status: "all" | "active" | "inactive";
}
const BLANK_FILTER: RewardsFilterState = {
  search: "",
  categoryId: "",
  status: "all",
};

function matchesFilter(reward: Reward, filter: RewardsFilterState) {
  if (filter.categoryId && reward.categoryId !== filter.categoryId)
    return false;
  if (filter.status === "active" && !reward.active) return false;
  if (filter.status === "inactive" && reward.active) return false;
  if (filter.search.trim()) {
    const needle = filter.search.trim().toLowerCase();
    if (!text(reward.name, "es").toLowerCase().includes(needle)) return false;
  }
  return true;
}

function defaultsFor(reward: Reward | null): RewardFormValues {
  if (!reward) {
    return {
      nameEs: "",
      nameEn: "",
      descriptionEs: "",
      descriptionEn: "",
      imageUrl: "",
      imageFileId: "",
      pointsCost: 0,
      categoryId: "",
      merchantIds: [],
      workshopIds: [],
      validFrom: "",
      validUntil: "",
      active: true,
    };
  }
  return {
    nameEs: text(reward.name, "es"),
    nameEn: text(reward.name, "en"),
    descriptionEs: text(reward.description ?? null, "es"),
    descriptionEn: text(reward.description ?? null, "en"),
    imageUrl: reward.imageUrl ?? "",
    imageFileId: "",
    pointsCost: reward.pointsCost,
    categoryId: reward.categoryId,
    merchantIds: [...(reward.merchantIds ?? [])],
    workshopIds: [...(reward.workshopIds ?? [])],
    validFrom: reward.validFrom ?? "",
    validUntil: reward.validUntil ?? "",
    active: reward.active,
  };
}

function buildInput(values: RewardFormValues): RewardInput {
  const name: BilingualText = {
    es: values.nameEs,
    ...(values.nameEn ? { en: values.nameEn } : {}),
  };
  const hasDescription = values.descriptionEs || values.descriptionEn;
  const description: BilingualText | undefined = hasDescription
    ? {
        es: values.descriptionEs,
        ...(values.descriptionEn ? { en: values.descriptionEn } : {}),
      }
    : undefined;
  return {
    name,
    ...(description ? { description } : {}),
    ...(values.imageUrl ? { imageUrl: values.imageUrl } : {}),
    // PLAN-007 Fase 4d: viaja solo cuando el operador subió un archivo nuevo
    // en esta sesión del formulario; el backend le da prioridad sobre
    // `imageUrl` (RewardInput.imageFileId).
    ...(values.imageFileId ? { imageFileId: values.imageFileId } : {}),
    pointsCost: values.pointsCost,
    categoryId: values.categoryId,
    ...placesAndValidityInput(values),
  };
}

// `RField` (etiqueta 12px 600 + slot de error) vive en
// `@portal/common/LabeledField` (PA-025-E): mismo componente palabra por
// palabra que usaba `BField` en benefits/BenefitsScreen.tsx. `GPField` en
// grand-prize/GrandPrizeConfigScreen.tsx queda fuera de este movimiento
// (deuda declarada de SPEC-210, PA-025-E).

interface RewardEditorProps {
  reward: Reward | null;
  categories: CatalogEntry[] | undefined;
  canWrite: boolean;
  submitting: boolean;
  onSubmit: (id: string | undefined, input: RewardInput) => void;
  onCancel: () => void;
  // Revisión en vivo 2026-09-22, puntos #3/#4A: el editor ES el diálogo. La
  // pantalla ya no lo envuelve en un `Modal`, le pasa acá el destino.
  modal: EditorModalTarget;
  // Mismo criterio que `CatalogEntryForm`/`MemberForm`: el 400 de
  // `descriptionEs` vacío con `descriptionEn` lleno (`reward.service.ts`,
  // `fieldRejection`) vive en `RewardsScreen` (dueño del `useToast`); acá se
  // intenta asociar con `applyApiFieldErrors` y, si no se puede, se devuelve
  // por `onUnhandledSubmitError` para el toast de siempre.
  submitError?: unknown;
  onUnhandledSubmitError?: (error: unknown) => void;
}

function RewardEditor({
  reward,
  categories,
  canWrite,
  submitting,
  onSubmit,
  onCancel,
  modal,
  submitError,
  onUnhandledSubmitError,
}: RewardEditorProps) {
  const [language, setLanguage] = useState<ContentLanguage>("es");
  const merchants = useEntries("merchants", false, "");
  const workshops = useEntries("workshops", false, "");
  const { register, handleSubmit, watch, setValue, errors, setError } =
    useEditorForm<RewardFormValues>(defaultsFor(reward));
  useApiFieldErrors(
    submitError,
    setError as (
      field: string,
      error: { type: string; message: string },
    ) => void,
    REWARD_FORM_FIELDS,
    onUnhandledSubmitError,
  );
  const nameEs = watch("nameEs");
  const imageUrl = watch("imageUrl");
  const merchantIds = watch("merchantIds");
  const workshopIds = watch("workshopIds");
  const active = watch("active");

  const { toggleMerchant, toggleWorkshop } = usePlacesToggle(
    merchantIds,
    workshopIds,
    (next) => setValue("merchantIds", next, { shouldDirty: true }),
    (next) => setValue("workshopIds", next, { shouldDirty: true }),
  );

  const submit = (values: RewardFormValues) => {
    const parsed = rewardFormSchema.safeParse(values);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        setError(issue.path[0] as keyof RewardFormValues, {
          message: issue.message,
        });
      }
      return;
    }
    onSubmit(reward?.id, buildInput(parsed.data));
  };

  return (
    <EditorModal
      modal={modal}
      onSubmit={handleSubmit(submit)}
      header={
        <EditorHeader
          eyebrow={reward ? "Editar recompensa" : "Nueva recompensa"}
          title={nameEs || "Recompensa sin nombre"}
          language={language}
          onLanguageChange={setLanguage}
          statusIndicator={
            <span className={`stat stat-${active ? "silver" : "neutral"}`}>
              {active ? "Activa" : "Inactiva"}
            </span>
          }
        />
      }
      footer={
        <EditorFooter
          onCancel={onCancel}
          action={reward ? "canEdit" : "canCreate"}
          disabled={submitting}
          submitting={submitting}
        />
      }
    >
      {/* El `padding` del cuerpo lo pone ahora la región del `Modal`
        (`values.modalRegions.bodyPadding`, los mismos 20px 24px que tenía
        este `fieldset`): si se dejara acá quedaría duplicado. */}
      <fieldset
        disabled={!canWrite}
        style={{ border: "none", margin: 0, padding: 0 }}
      >
        <RField
          label="Nombre"
          required={language === "es"}
          error={language === "es" ? errors.nameEs?.message : undefined}
        >
          <input
            key={language}
            {...register(language === "es" ? "nameEs" : "nameEn")}
            placeholder={
              language === "en"
                ? "Sin traducir · se mostrará en español"
                : undefined
            }
            style={inputStyle}
          />
        </RField>
        <RField
          label="Descripción"
          error={language === "es" ? errors.descriptionEs?.message : undefined}
        >
          <textarea
            key={language}
            {...register(language === "es" ? "descriptionEs" : "descriptionEn")}
            placeholder={
              language === "en"
                ? "Sin traducir · se mostrará en español"
                : undefined
            }
            style={{ ...inputStyle, minHeight: 74, resize: "vertical" }}
          />
        </RField>
        <RFieldGroup
          label="Foto"
          hint="La imagen es lo primero que el miembro ve en el catálogo de canje."
        >
          <ImageUploadBox>
            <ImagePreviewBox imageUrl={imageUrl} />
            <div
              style={{
                flex: 1,
                font: `400 11.5px/1.5 ${tokens.font.ui}`,
                color: tokens.text.tertiary,
              }}
            >
              La imagen es lo primero que el miembro ve en el catálogo de canje.
            </div>
            <FileUploadChip
              entityType="reward"
              label="Reemplazar"
              ariaLabel="Subir foto de la recompensa"
              onUploaded={(image) => {
                // PLAN-007 Fase 4d: `imageUrl` solo para la
                // previsualización inmediata (vigente 15 minutos); el
                // `fileId` es lo que de verdad viaja al servidor.
                setValue("imageUrl", image.imageUrl, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                setValue("imageFileId", image.fileId, {
                  shouldDirty: true,
                });
              }}
            />
          </ImageUploadBox>
        </RFieldGroup>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "0 16px",
          }}
        >
          <RField
            label="Costo en puntos"
            required
            error={errors.pointsCost?.message}
          >
            <input
              type="number"
              min={1}
              placeholder="5000"
              {...register("pointsCost", { valueAsNumber: true })}
              style={inputStyle}
            />
          </RField>
          <RField label="Categoría" required error={errors.categoryId?.message}>
            <select {...register("categoryId")} style={inputStyle}>
              <option value="">Selecciona una categoría</option>
              {categories?.map((category) => (
                <option key={category.id} value={category.id}>
                  {text(category.name, "es")}
                </option>
              ))}
            </select>
          </RField>
        </div>
        <ValidityRangeFields
          validFrom={register("validFrom")}
          validUntil={register("validUntil")}
          untilError={errors.validUntil?.message}
        />
        {/* PA-023-C: lugares de consumo (ANNEX-011 §2.1), sin equivalente en
            el prototipo congelado — hueco resuelto por la resolución humana
            PA-023, no una reinterpretación del look. */}
        <PlacesFieldGroup
          merchants={merchants}
          merchantIds={merchantIds}
          onToggleMerchant={toggleMerchant}
          workshops={workshops}
          workshopIds={workshopIds}
          onToggleWorkshop={toggleWorkshop}
        />
        {errors.merchantIds?.message ? (
          <FieldError message={errors.merchantIds.message} />
        ) : !merchantIds.length && !workshopIds.length ? (
          <InlineNote>
            Asocia al menos un comercio aliado o un taller: sin lugar, la
            recompensa no podría consumirse.
          </InlineNote>
        ) : null}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: 4,
          }}
        >
          <span className="label">Estado</span>
          <div
            style={{ display: "flex", gap: 10 }}
            role="group"
            aria-label="Estado"
          >
            {(["Activa", "Inactiva"] as const).map((label) => (
              <button
                key={label}
                type="button"
                onClick={() =>
                  setValue("active", label === "Activa", {
                    shouldDirty: true,
                  })
                }
                className={
                  "chip" +
                  ((active ? "Activa" : "Inactiva") === label ? " on" : "")
                }
                style={{ padding: "11px 20px", fontSize: 13 }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </fieldset>
    </EditorModal>
  );
}

function RewardCard({
  reward,
  categoryText,
  canEdit,
  canInactivate,
  onEdit,
  onToggleActive,
}: {
  reward: Reward;
  categoryText: string;
  canEdit: boolean;
  canInactivate: boolean;
  onEdit: () => void;
  onToggleActive: () => void;
}) {
  return (
    <div
      style={{
        border: `1px solid ${tokens.surface.line}`,
        borderRadius: 18,
        background: tokens.surface.bg2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        opacity: reward.active ? 1 : 0.58,
      }}
    >
      <div style={{ position: "relative" }}>
        <ImageWithPlaceholder
          src={reward.imageUrl}
          icon="gift"
          iconSize={26}
          showText={false}
          imageStyle={{
            width: "100%",
            height: 152,
            objectFit: "cover",
            display: "block",
            background: tokens.surface.surface,
          }}
        />
        <span
          className={`stat stat-${reward.active ? "silver" : "neutral"}`}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            backdropFilter: "blur(6px)",
          }}
        >
          {reward.active ? "Activa" : "Inactiva"}
        </span>
      </div>
      <div
        style={{
          padding: 16,
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <span
          className="stat stat-neutral"
          style={{ alignSelf: "flex-start", marginBottom: 10 }}
        >
          {categoryText}
        </span>
        <div
          style={{
            font: `600 14px/1.35 ${tokens.font.ui}`,
            color: tokens.text.primary,
          }}
        >
          {text(reward.name, "es")}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 6,
            margin: "14px 0 12px",
            flex: 1,
            alignContent: "flex-end",
          }}
        >
          <span
            className="display"
            style={{
              fontSize: 27,
              color: tokens.gold.light,
              lineHeight: 1,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatPoints(reward.pointsCost)}
          </span>
          <span
            style={{
              font: `600 10.5px ${tokens.font.ui}`,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: tokens.text.tertiary,
            }}
          >
            puntos
          </span>
        </div>
        <div
          style={{
            display: "flex",
            gap: 6,
            borderTop: `1px solid ${tokens.surface.line}`,
            paddingTop: 12,
          }}
        >
          {canEdit ? (
            <RowIconAction label="Editar" icon="edit" onClick={onEdit} />
          ) : null}
          {/* Inactivar y Activar exigen el mismo permiso en el servidor
            (corregido por SEC-05, 2026-09-10): el middleware
            require-inactivate-for-active-change.ts exige `inactivate` para
            mover `active` en CUALQUIER dirección, no solo al apagar — el
            gating anterior (Activar con canEdit) venía de antes de que SEC-01
            endureciera el backend en las dos direcciones y quedó obsoleto:
            un perfil "Operador sin inactivar" (canEdit sin canInactivate,
            SPEC-210 §7) veía el botón Activar y recibía 403 siempre.
            canEdit sigue alcanzando para Editar, que no mueve `active`. */}
          {canInactivate ? (
            reward.active ? (
              <RowIconAction
                label="Inactivar"
                icon="deactivate"
                tone="danger"
                onClick={onToggleActive}
              />
            ) : (
              <RowIconAction
                label="Activar"
                icon="check"
                onClick={onToggleActive}
              />
            )
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function RewardsScreen() {
  const toast = useToast();
  const { hasPermission } = useSession();
  const rewards = useRewards();
  const categories = useEntries("reward-categories", false, "");
  const mutations = useRewardMutations();
  const [filter, setFilter] = useState<RewardsFilterState>(BLANK_FILTER);
  const [editing, setEditingState] = useState<Reward | null | undefined>(
    undefined,
  );
  // Hallazgo 2026-09-29: `RewardEditor` ya tenía `useApiFieldErrors`, pero la
  // pantalla nunca le pasaba `submitError`, así que ningún rechazo por campo
  // de la API (`descriptionEs`, `validUntil`) llegaba a su input — caía al
  // toast del fallback global. Mismo cableado que `OffersScreen`, salvo que
  // abrir o cerrar el editor limpia el rechazo: si no, un editor recién
  // montado volvería a aplicar el error del intento anterior.
  const [submitError, setSubmitError] = useState<unknown>(undefined);
  const setEditing = (next: Reward | null | undefined) => {
    setSubmitError(undefined);
    setEditingState(next);
  };

  const canCreate = hasPermission("content", "canCreate");
  const canEdit = hasPermission("content", "canEdit");
  const canInactivate = hasPermission("content", "canInactivate");

  const list = rewards.data;
  const rows = useMemo(
    () => (list ?? []).filter((reward) => matchesFilter(reward, filter)),
    [list, filter],
  );

  const submitting = mutations.create.isPending || mutations.edit.isPending;

  const onSaved = (reward: Reward) => {
    setEditing(undefined);
    toast.show(`${text(reward.name, "es")} guardada y publicada`);
    for (const warning of reward.warnings ?? [])
      toast.show(warning.message, "neutral");
  };

  // PLAN-009 §3: COST_INVALID es un rechazo de regla de negocio (el costo
  // capturado no es válido, no un error de servicio) — se queda en tono
  // `neutral`, y marca el error atendido para que el fallback global no
  // duplique el toast con el genérico. Cualquier otro código va al editor, que lo asocia a su campo o lo
  // devuelve por `onUnhandledSubmitError` para el toast de error: se marca
  // atendido acá, en el mismo tick, para que el fallback global no hable
  // además (el editor lo resuelve en un efecto, después del macrotask).
  const onSubmit = (id: string | undefined, input: RewardInput) => {
    const handleError = (error: unknown) => {
      markErrorHandled(error);
      if (error instanceof ApiError && error.code === "COST_INVALID") {
        toast.show("El costo en puntos debe ser mayor que cero", "neutral");
        return;
      }
      setSubmitError(error);
    };
    submitCreateOrEdit(id, input, mutations, onSaved, handleError);
  };

  const toggleActive = (reward: Reward) => {
    if (reward.active) {
      mutations.inactivate.mutate(reward.id, {
        onSuccess: () => toast.show(`${text(reward.name, "es")} inactivada`),
      });
      return;
    }
    mutations.edit.mutate(
      {
        id: reward.id,
        input: {
          name: reward.name,
          ...(reward.description ? { description: reward.description } : {}),
          ...(reward.imageUrl ? { imageUrl: reward.imageUrl } : {}),
          pointsCost: reward.pointsCost,
          categoryId: reward.categoryId,
          merchantIds: reward.merchantIds ?? [],
          workshopIds: reward.workshopIds ?? [],
          ...(reward.validFrom ? { validFrom: reward.validFrom } : {}),
          ...(reward.validUntil ? { validUntil: reward.validUntil } : {}),
          active: true,
        },
      },
      {
        onSuccess: (updated) => {
          toast.show(`${text(updated.name, "es")} activada`);
          for (const warning of updated.warnings ?? [])
            toast.show(warning.message, "neutral");
        },
      },
    );
  };

  const clearFilters = () => setFilter(BLANK_FILTER);
  const hasAnyReward = (list?.length ?? 0) > 0;

  return (
    <div>
      {/* TABS-A (human-resolution-tabs-pattern.json): Recompensas comparte el
        encabezado y la fila de pestañas de "Gestión de Premios"
        (PortalPrimitives.tsx:453-479), igual que /prizes/configuration — el
        encabezado propio que tenía esta pantalla medía 0.2433 de deriva
        contra el baseline canónico por la ausencia de esa fila. */}
      <PrizesScreenToolbar
        filter={filter}
        setFilter={setFilter}
        searchPlaceholder="Buscar recompensa"
        extraFilters={
          // CAT-01 (human-resolution-categories-and-schema.json): las
          // categorías de recompensa se administran únicamente en Catálogos
          // (SPEC-011, web/src/portal/features/catalogs/CatalogsScreen.tsx).
          // El chip «Categorías» y su modal se retiraron de acá: el
          // prototipo no tiene disparador real (`setCatMod(true)` no existe
          // en admin.jsx:2287-2483) y duplicar el alta/baja del catálogo en
          // dos pantallas es la fuente de comportamientos divergentes que
          // CAT-01 quiso evitar. El selector de categoría de este filtro y
          // el del editor se quedan: consumen el catálogo, no lo
          // administran.
          <CategoryFilterSelect
            value={filter.categoryId}
            onChange={(categoryId) =>
              setFilter((prev) => ({ ...prev, categoryId }))
            }
            categories={categories.data}
          />
        }
        createLabel="Nueva recompensa"
        onCreate={() => setEditing(null)}
      />

      {rewards.isLoading ? (
        <div style={{ padding: 24, color: tokens.text.tertiary }}>
          Cargando…
        </div>
      ) : rows.length === 0 ? (
        <div
          style={{
            border: `1px solid ${tokens.surface.line}`,
            borderRadius: 18,
            background: tokens.surface.bg2,
          }}
        >
          <EmptyResultsPanel
            icon="gift"
            title={
              hasAnyReward
                ? "Ninguna recompensa coincide"
                : "Aún no hay recompensas"
            }
            description={
              hasAnyReward
                ? "Ajuste la búsqueda, la categoría o el estado para ver más resultados."
                : "El catálogo de canje queda oculto en la app hasta que exista al menos una recompensa activa."
            }
            actions={
              <EmptyResultsActions
                hasAny={hasAnyReward}
                onClearFilters={clearFilters}
                onCreate={() => setEditing(null)}
                createLabel="Nueva recompensa"
              />
            }
          />
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(258px, 1fr))",
            gap: 16,
          }}
        >
          {rows.map((reward) => (
            <RewardCard
              key={reward.id}
              reward={reward}
              categoryText={categoryLabel(categories.data, reward.categoryId)}
              canEdit={canEdit}
              canInactivate={canInactivate}
              onEdit={() => setEditing(reward)}
              onToggleActive={() => toggleActive(reward)}
            />
          ))}
        </div>
      )}

      {editing !== undefined ? (
        <RewardEditor
          key={editing?.id ?? "new"}
          reward={editing}
          categories={categories.data}
          canWrite={canCreate || canEdit}
          submitting={submitting}
          onSubmit={onSubmit}
          onCancel={() => setEditing(undefined)}
          submitError={submitError}
          onUnhandledSubmitError={(error) => reportError(toast, error)}
          modal={{
            label: editing?.id ? "Editar recompensa" : "Nueva recompensa",
            width: 700,
            onClose: () => setEditing(undefined),
          }}
        />
      ) : null}
    </div>
  );
}
